import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ImportData from "@/pages/admin/ImportData";
import DataOrangTua from "@/pages/admin/DataOrangTua";
import DataSantri from "@/pages/admin/DataSantri";
import KeuTagihan from "@/pages/admin/KeuTagihan";
import KeuKebutuhan from "@/pages/admin/KeuKebutuhan";
import KeuTabungan from "@/pages/admin/KeuTabungan";
import KeuLaporan from "@/pages/admin/KeuLaporan";
import ParentDashboard from "@/pages/parent/ParentDashboard";
import StudentDetail from "@/pages/parent/StudentDetail";
import ParentHistory from "@/pages/parent/ParentHistory";
import Assistant from "@/pages/parent/Assistant";
import PaymentReturn from "@/pages/parent/PaymentReturn";
import PaymentInstructions from "@/pages/parent/PaymentInstructions";
import AdminQrCards from "@/pages/admin/AdminQrCards";
import AdminAscCards from "@/pages/admin/AdminAscCards";
import AdminAscReport from "@/pages/admin/AdminAscReport";
import AdminMarketDay from "@/pages/admin/AdminMarketDay";
import AdminStaff from "@/pages/admin/AdminStaff";
import DataPendidik from "@/pages/admin/DataPendidik";
import LaporanBelanjaPendidik from "@/pages/admin/LaporanBelanjaPendidik";
import KartuQrPendidik from "@/pages/admin/KartuQrPendidik";
import AdminSaldoSantri from "@/pages/admin/AdminSaldoSantri";
import AdminLedgerReport from "@/pages/admin/AdminLedgerReport";
import AdminTransactions from "@/pages/admin/AdminTransactions";
import ParentBills from "@/pages/parent/ParentBills";
import ParentLedger from "@/pages/parent/ParentLedger";
import POS from "@/pages/cashier/POS";
import Products from "@/pages/cashier/Products";
import Sales from "@/pages/cashier/Sales";
import Terms from "@/pages/public/Terms";
import Refund from "@/pages/public/Refund";
import Faq from "@/pages/public/Faq";
import Contact from "@/pages/public/Contact";
import VerifyBill from "@/pages/public/VerifyBill";

function Guard({ roles, children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center text-stone-400">
        Memuat…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <RoleHome />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "parent") return <Navigate to="/parent" replace />;
  return <Navigate to="/pos" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RoleHome />} />
            <Route path="/login" element={<Login />} />
            <Route path="/payment/return" element={<PaymentReturn />} />
            <Route path="/payment/instructions" element={<PaymentInstructions />} />
            <Route path="/syarat-ketentuan" element={<Terms />} />
            <Route path="/kebijakan-refund" element={<Refund />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/kontak" element={<Contact />} />
            <Route path="/verify/:id" element={<VerifyBill />} />

            <Route element={<Guard roles={["admin"]}><Layout /></Guard>}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/import" element={<ImportData />} />
              <Route path="/admin/parents" element={<DataOrangTua />} />
              <Route path="/admin/students" element={<DataSantri />} />
              <Route path="/admin/bills" element={<KeuTagihan />} />
              <Route path="/admin/tabungan" element={<KeuTabungan />} />
              <Route path="/admin/laporan" element={<KeuLaporan />} />
              <Route path="/admin/transaksi-online" element={<AdminTransactions />} />
              <Route path="/admin/qr" element={<AdminQrCards />} />
              <Route path="/admin/staff" element={<AdminStaff />} />
              <Route path="/admin/pendidik" element={<DataPendidik />} />
              <Route path="/admin/pendidik/qr" element={<KartuQrPendidik />} />
              <Route path="/admin/pendidik/laporan" element={<LaporanBelanjaPendidik />} />
              <Route path="/admin/kop/saldo" element={<KeuKebutuhan />} />
              <Route path="/admin/kop/history-saldo" element={<AdminSaldoSantri />} />
              <Route path="/admin/rekap-tabungan" element={<AdminLedgerReport category="tabungan" title="Rekap Tabungan" subtitle="Mutasi setoran & penarikan tabungan santri berdasarkan rentang tanggal dan petugas." withPending showChart />} />
              <Route path="/admin/kop/asc" element={<AdminAscCards />} />
              <Route path="/admin/kop/asc-laporan" element={<AdminAscReport />} />
              <Route path="/admin/kop/market-day" element={<AdminMarketDay />} />
              <Route path="/admin/kop/stok" element={<Products />} />
              <Route path="/admin/kop/laporan" element={<Sales />} />
              <Route path="/admin/kop/rekap-saldo-belanja" element={<AdminLedgerReport category="saldo" title="Rekap Uang Jajan & Belanja" subtitle="Mutasi uang jajan santri, utang/pembayaran pendidik, dan kartu ASC (top-up & belanja Kopontren) berdasarkan rentang tanggal dan petugas." setoranLabel="Top-up" penarikanLabel="Belanja" buyerLabel="Nama Pembeli" idLabel="Nomor Kartu" showChart />} />
            </Route>

            <Route element={<Guard roles={["parent"]}><Layout /></Guard>}>
              <Route path="/parent" element={<ParentDashboard />} />
              <Route path="/parent/students/:id" element={<StudentDetail />} />
              <Route path="/parent/tagihan" element={<ParentBills />} />
              <Route path="/parent/history-saldo" element={<ParentLedger />} />
              <Route path="/parent/bayar" element={<ParentBills />} />
              <Route path="/parent/riwayat" element={<ParentHistory />} />
              <Route path="/parent/assistant" element={<Assistant />} />
            </Route>

            <Route element={<Guard roles={["cashier", "admin"]}><Layout /></Guard>}>
              <Route path="/pos" element={<POS />} />
              <Route path="/pos/products" element={<Products />} />
              <Route path="/pos/sales" element={<Sales />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
